import {
    type ReactNode,
    useId,
    useLayoutEffect,
    useMemo,
    useState,
} from "react";
import { createPortal } from "react-dom";
import {
    type AutoScrollOptions,
    DndContext,
    type DragStartEvent,
    DragOverlay,
    type DropAnimation,
    KeyboardSensor,
    type KeyboardSensorOptions,
    type Modifier,
    PointerSensor,
    useDraggable,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import type { RowData } from "@tanstack/react-table";
import { LiveRegion, useAnnouncer } from "@design-system/LiveRegion/LiveRegion";
import { SILENT_DND_ANNOUNCEMENTS } from "@design-system/LiveRegion/silentDndAnnouncements";
import type { GridController } from "../../focus/gridController";
import { readTableMotion, type TableMotion } from "../../motion/tableMotion";
import type { DataGridInstance } from "../../types";
import {
    COLUMN_DRAG_ID,
    ColumnDragContext,
    ROW_DRAG_ID,
    RowDragContext,
} from "./dragContext";
import {
    ColumnDropIndicator,
    DragPreview,
    RowDropIndicator,
} from "./DragFeedback";
import type { DraggableApiRef, DragPreviewInfo } from "./dragKind";
import { getDropAnimation } from "./dropAnimation";
import { useColumnDragController } from "./useColumnDragController";
import { useRowDragController } from "./useRowDragController";

const INSTRUCTIONS =
    "To reorder, press Space to pick the row up, the up and down arrow keys to move it, Space to drop it, or Escape to cancel.";

const lockX: Modifier = ({ transform }) => ({ ...transform, x: 0 });
const lockY: Modifier = ({ transform }) => ({ ...transform, y: 0 });
const ROW_MODIFIERS = [lockX];
const COLUMN_MODIFIERS = [lockY];

// A press that moves less than this stays a click (sorting, the menu).
const POINTER_OPTIONS = { activationConstraint: { distance: 4 } };

// Above popovers, like the view toolbar's column list overlay.
const OVERLAY_Z_INDEX = 1061;

interface OverlayState extends DragPreviewInfo {
    kind: "row" | "column";
    motion: TableMotion | null;
    dropAnimation: DropAnimation | null;
}

/** The single draggable a drag kind re-points at whatever was pressed. */
const DragSource = ({
    id,
    apiRef,
}: {
    id: string;
    apiRef: DraggableApiRef;
}) => {
    const { setNodeRef, setActivatorNodeRef, listeners } = useDraggable({ id });
    useLayoutEffect(() => {
        apiRef.current = { setNodeRef, setActivatorNodeRef, listeners };
    });
    return null;
};

interface GridDndProps<TData extends RowData> {
    instance: DataGridInstance<TData>;
    controller: GridController;
    children: ReactNode;
}

const GridDndProvider = <TData extends RowData>({
    instance,
    controller,
    children,
}: Readonly<GridDndProps<TData>>) => {
    const instructionsId = useId();
    const [overlay, setOverlay] = useState<OverlayState | null>(null);
    const { announcement, announce } = useAnnouncer();
    const rows = useRowDragController({
        instance,
        controller,
        announce,
        instructionsId,
    });
    const columns = useColumnDragController({
        instance,
        controller,
        announce,
    });

    const keyboardOptions = useMemo(
        (): KeyboardSensorOptions => ({
            coordinateGetter: rows.coordinateGetter,
            scrollBehavior: "auto",
        }),
        [rows.coordinateGetter],
    );
    const sensors = useSensors(
        useSensor(PointerSensor, POINTER_OPTIONS),
        useSensor(KeyboardSensor, keyboardOptions),
    );

    const autoScroll: AutoScrollOptions = {
        canScroll: (element) => element === controller.viewport,
        threshold:
            overlay?.kind === "column" ? { x: 0.15, y: 0 } : { x: 0, y: 0.15 },
    };

    const handleDragStart = ({ active, activatorEvent }: DragStartEvent) => {
        const kind = active.id === ROW_DRAG_ID ? "row" : "column";
        const preview = (kind === "row" ? rows : columns).onStart(
            activatorEvent,
        );
        if (!preview) {
            return;
        }
        controller.viewport?.setAttribute("data-dragging", "");
        const motion = controller.root
            ? readTableMotion(controller.root)
            : null;
        setOverlay({
            kind,
            ...preview,
            motion,
            dropAnimation: getDropAnimation({ motion, input: preview.input }),
        });
    };

    const handleDragEnd = () => {
        controller.viewport?.removeAttribute("data-dragging");
        rows.onEnd();
        columns.onEnd();
    };

    const handleDragCancel = () => {
        controller.viewport?.removeAttribute("data-dragging");
        rows.onCancel();
        columns.onCancel();
    };

    return (
        <DndContext
            sensors={sensors}
            autoScroll={autoScroll}
            accessibility={{
                announcements: SILENT_DND_ANNOUNCEMENTS,
                screenReaderInstructions: { draggable: INSTRUCTIONS },
            }}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            {instance.hasRowReorder ? (
                <DragSource id={ROW_DRAG_ID} apiRef={rows.sourceRef} />
            ) : null}
            {instance.hasColumnReorder ? (
                <DragSource id={COLUMN_DRAG_ID} apiRef={columns.sourceRef} />
            ) : null}
            <RowDragContext.Provider
                value={instance.hasRowReorder ? rows.contextValue : null}
            >
                <ColumnDragContext.Provider
                    value={
                        instance.hasColumnReorder ? columns.contextValue : null
                    }
                >
                    {children}
                </ColumnDragContext.Provider>
            </RowDragContext.Provider>
            <RowDropIndicator
                indicator={rows.indicator}
                input={overlay?.input ?? "pointer"}
            />
            <ColumnDropIndicator indicator={columns.indicator} />
            {createPortal(
                <>
                    <DragOverlay
                        dropAnimation={overlay?.dropAnimation ?? null}
                        modifiers={
                            overlay?.kind === "column"
                                ? COLUMN_MODIFIERS
                                : ROW_MODIFIERS
                        }
                        zIndex={OVERLAY_Z_INDEX}
                    >
                        {overlay?.input === "pointer" ? (
                            <DragPreview
                                kind={overlay.kind}
                                preview={overlay}
                                motion={overlay.motion}
                            />
                        ) : null}
                    </DragOverlay>
                    <div id={instructionsId} hidden>
                        {INSTRUCTIONS}
                    </div>
                    <LiveRegion
                        announcement={announcement}
                        politeness="assertive"
                    />
                </>,
                document.body,
            )}
        </DndContext>
    );
};

/**
 * Drag and drop for rows (`rowReorder`) and headers (`columnReorder`) under
 * one DndContext. The grid's parts render as `children` elements, so drag
 * state changes here never re-render them.
 */
export const GridDnd = <TData extends RowData>(
    props: Readonly<GridDndProps<TData>>,
) =>
    props.instance.hasRowReorder || props.instance.hasColumnReorder ? (
        <GridDndProvider {...props} />
    ) : (
        props.children
    );
